import { FC, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type {
  ScoringType,
  CriterionKey,
  ProductIntensityBand,
  ProductInstallAgeBand,
  ProductPresenceBand,
} from "@/lib/scoring/types";
import {
  CRITERION_LABELS,
  CRITERION_TOOLTIPS,
  CRITERION_REGISTRY,
  getCriterionKey,
} from "@/lib/scoring/criterionRegistry";
import { useGetIntentTopicsByIdsQuery } from "@/generated/taxonomy";
import { useQuery } from "@apollo/client";
import { SIMPLIFIED_PRODUCT_ID_QUERY } from "@/apis/taxonomy/queries";
import type { ProductsQueryPayload } from "@/types/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { useHumanReadableBandRows } from "./useHumanReadableBandRows";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";

const WEIGHT_TOOLTIP =
  "Weights indicate the proportion of this scoring type relative to others configured";

function getIntentTopicsFromCriterion(
  criterion: ScoringType,
  key: CriterionKey | null
): { topicIds: string[]; inclusionMethod: string | null } {
  if (!key) return { topicIds: [], inclusionMethod: null };
  const bands = criterion[key] as
    | Array<{ topics?: { ids?: string[]; inclusionMethod?: string } }>
    | undefined;
  const firstBand = bands?.[0];
  return {
    topicIds: firstBand?.topics?.ids ?? [],
    inclusionMethod: firstBand?.topics?.inclusionMethod ?? null,
  };
}

interface CriterionReadOnlyViewProps {
  criterion: ScoringType;
  onEdit: () => void;
  onDelete: () => void;
  onWeightChange: (weight: number | undefined) => void;
}

export const CriterionReadOnlyView: FC<CriterionReadOnlyViewProps> = ({
  criterion,
  onEdit,
  onDelete,
  onWeightChange,
}) => {
  const key = getCriterionKey(criterion);
  const typeLabel = key ? CRITERION_LABELS[key] : "Unknown";
  const criteriaTooltip = key ? CRITERION_TOOLTIPS[key] : undefined;
  const isIntent = key ? CRITERION_REGISTRY[key]?.isFit === false : false;
  const isSpend = key === "spend";
  const isCategoryPenetration = key === "productCategoryPenetration";
  const isVendorPenetration = key === "vendorPenetration";

  // Resolved band rows for all criteria (intent + fit)
  const { bandRows: resolvedBandRows, contextHeader } =
    useHumanReadableBandRows(criterion, key);

  const { spendCategories, productCategories, vendors } = useTaxonomyContext();
  const spendCategoryId = isSpend
    ? (criterion.spend?.[0]?.categoryId ?? null)
    : null;

  const categoryName = useMemo(() => {
    if (!isCategoryPenetration) return null;
    const categoryId = criterion.productCategoryPenetration?.[0]?.categoryId;
    if (!categoryId) return null;
    return (
      productCategories.find((c) => String(c.id) === categoryId)?.name ??
      categoryId
    );
  }, [isCategoryPenetration, criterion, productCategories]);

  const vendorName = useMemo(() => {
    if (!isVendorPenetration) return null;
    const vendorId = criterion.vendorPenetration?.[0]?.vendorId;
    if (!vendorId) return null;
    return vendors.find((v) => v.id === vendorId)?.name ?? String(vendorId);
  }, [isVendorPenetration, criterion, vendors]);
  const spendCategoryName = spendCategoryId
    ? (spendCategories.find((c) => c.id === spendCategoryId)?.name ??
      spendCategoryId)
    : null;

  // Intent topics — still needed for the TOPICS chip section
  const { topicIds: intentTopicIds, inclusionMethod } = useMemo(
    () => getIntentTopicsFromCriterion(criterion, isIntent ? key : null),
    [criterion, key, isIntent]
  );

  const { data: topicsData, loading: topicsLoading } =
    useGetIntentTopicsByIdsQuery({
      variables: { ids: intentTopicIds as [string, ...string[]] },
      skip: intentTopicIds.length === 0,
      context: { clientName: TAXONOMY_CLIENT },
      fetchPolicy: "cache-first",
    });

  const topicNames = useMemo(() => {
    if (!topicsData?.intentTopic?.data) return [];
    return topicsData.intentTopic.data
      .filter(
        (t): t is NonNullable<typeof t> => t !== null && t.displayName !== null
      )
      .map((t) => t.displayName as string);
  }, [topicsData]);

  // Product names — still needed for the PRODUCTS chip section
  const isProductCriterion =
    key === "productIntensity" ||
    key === "productInstallAgeMonths" ||
    key === "products";
  const productIds = useMemo(() => {
    if (!isProductCriterion || !key) return [];
    if (key === "products") {
      const bands = criterion.products as ProductPresenceBand[] | undefined;
      return [...new Set(bands?.flatMap((b) => b.ids) ?? [])];
    }
    const bands = criterion[key] as
      | (ProductIntensityBand | ProductInstallAgeBand)[]
      | undefined;
    return bands?.[0]?.productIds ?? [];
  }, [criterion, key, isProductCriterion]);

  const { data: productsData, loading: productsLoading } =
    useQuery<ProductsQueryPayload>(SIMPLIFIED_PRODUCT_ID_QUERY, {
      variables: { ids: productIds },
      skip: productIds.length === 0,
      context: { clientName: TAXONOMY_CLIENT },
      fetchPolicy: "cache-first",
    });

  const productNames = useMemo(() => {
    if (!productsData?.product?.data) return [];
    return productsData.product.data
      .filter((p) => p.name != null)
      .map((p) => p.name);
  }, [productsData]);

  const [weightValue, setWeightValue] = useState<string>(
    criterion.weight !== undefined ? String(criterion.weight) : ""
  );

  return (
    <Box>
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>Scoring type:</strong> {typeLabel}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            size="small"
            color="primary"
            onClick={onEdit}
            data-testid="criterion-edit-btn"
          >
            EDIT
          </Button>
          <Button
            size="small"
            color="error"
            onClick={onDelete}
            data-testid="criterion-delete-btn"
          >
            DELETE
          </Button>
        </Box>
      </Box>

      {/* Name + weight row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {criterion.name || typeLabel}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={WEIGHT_TOOLTIP} placement="top">
            <IconButton size="small" tabIndex={-1}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <TextField
            label="Weight"
            size="small"
            value={weightValue}
            sx={{ width: 96 }}
            inputProps={{
              min: 0,
              max: 100000,
              "data-testid": "scoring-criterion-weight-input",
            }}
            onChange={(e) => setWeightValue(e.target.value)}
            onBlur={() => {
              const n = Number(weightValue);
              if (weightValue === "" || isNaN(n)) {
                onWeightChange(undefined);
              } else {
                onWeightChange(Math.min(100000, Math.max(0, n)));
              }
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Intent topics summary */}
      {isIntent && intentTopicIds.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.8,
              display: "block",
              mb: 0.75,
            }}
          >
            TOPICS
          </Typography>
          {inclusionMethod && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75 }}
            >
              Matching{" "}
              <strong>
                {inclusionMethod === "ALL_PRESENT" ? "all" : "any"}
              </strong>{" "}
              of the selected topics
            </Typography>
          )}
          {topicsLoading ? (
            <Skeleton variant="rectangular" height={32} />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {topicNames.map((name) => (
                <Chip key={name} label={name} size="small" />
              ))}
              {topicNames.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {intentTopicIds.length} topic
                  {intentTopicIds.length !== 1 ? "s" : ""} selected
                </Typography>
              )}
            </Box>
          )}
          <Divider sx={{ mt: 2, mb: 2 }} />
        </Box>
      )}

      {/* Product names summary */}
      {isProductCriterion && productIds.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.8,
              display: "block",
              mb: 0.75,
            }}
          >
            PRODUCTS
          </Typography>
          {productsLoading ? (
            <Skeleton variant="rectangular" height={32} />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {productNames.map((name) => (
                <Chip key={name} label={name} size="small" />
              ))}
              {productNames.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {productIds.length} product
                  {productIds.length !== 1 ? "s" : ""} selected
                </Typography>
              )}
            </Box>
          )}
          <Divider sx={{ mt: 2, mb: 2 }} />
        </Box>
      )}

      {/* IT Spend category summary */}
      {isSpend && spendCategoryName && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.8,
              display: "block",
              mb: 0.75,
            }}
          >
            CATEGORY
          </Typography>
          <Chip label={spendCategoryName} size="small" />
          <Divider sx={{ mt: 2, mb: 2 }} />
        </Box>
      )}

      {/* Category penetration summary */}
      {isCategoryPenetration && categoryName && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.8,
              display: "block",
              mb: 0.75,
            }}
          >
            PRODUCT CATEGORY
          </Typography>
          <Chip label={categoryName} size="small" />
          <Divider sx={{ mt: 2, mb: 2 }} />
        </Box>
      )}

      {/* Vendor penetration summary */}
      {isVendorPenetration && vendorName && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.8,
              display: "block",
              mb: 0.75,
            }}
          >
            VENDOR
          </Typography>
          <Chip label={vendorName} size="small" />
          <Divider sx={{ mt: 2, mb: 2 }} />
        </Box>
      )}

      {/* Context header from hook — skip for product/intent criteria since chips already show above */}
      {contextHeader && !isProductCriterion && !isIntent && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {contextHeader}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>
      )}

      {/* CRITERIA / POINTS columns */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: 0.8 }}
          >
            CRITERIA
          </Typography>
          {criteriaTooltip && (
            <Tooltip title={criteriaTooltip} placement="top">
              <IconButton size="small" tabIndex={-1}>
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.8 }}
        >
          POINTS
        </Typography>
      </Box>

      {/* Band rows */}
      {resolvedBandRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No bands configured.
        </Typography>
      ) : (
        resolvedBandRows.map((row, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: 0.75,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box>
              <Typography variant="body2">
                {/^Has (any|all) of /.test(row.summary) ? (
                  <>
                    Has{" "}
                    <strong>
                      {row.summary.startsWith("Has any") ? "any" : "all"}
                    </strong>{" "}
                    of {row.summary.replace(/^Has (?:any|all) of /, "")}
                  </>
                ) : (
                  row.summary
                )}
              </Typography>
              {row.subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {row.subtitle}
                </Typography>
              )}
            </Box>
            <Typography variant="body2">{row.score}</Typography>
          </Box>
        ))
      )}
    </Box>
  );
};
